import * as React from 'react';
import { connect } from 'react-redux';
import {
    Modal,
    Button,
    FormControl,
    FormGroup,
    ControlLabel,
    HelpBlock,
    Tab,
    Nav,
    NavItem
} from 'react-bootstrap';
import { Translate } from '../../../shared/LanguageService';
import * as BrokerFirmGroupActions from '../actions/BrokerFirmGroupActions';
import * as BrokerTreeTypes from '../BrokerTreeTypes';
import { assign } from '../../../shared/Assign';
import notify from '../../../shared/feedback/notifications';
import { handleError } from '../../../shared/ErrorCatching';
import FormInputChangeHandler from '../../../shared/form/FormInputChangeHandler';
import FormValidationService from '../../../shared/form/FormValidationService';
import ProductList from '../products/ProductList';
import FormSelect from '../../../components/common/FormSelect';
import SelectPlanTemplate from '../SelectPlanTemplate';

class FirmGroupModal extends React.Component<any, any> {
    formInputChangeHandler: FormInputChangeHandler;

    constructor(props: any, context: any) {
        super(props, context);

        this.state = {
            firmGroup: {
                type: BrokerTreeTypes.FirmGroup,
                parentId: props.item.parentId,
                products: [],
                name: ''
            },
            errors: {},
            creating: false
        };

        this.closeModal = this.closeModal.bind(this);
        this.save = this.save.bind(this);
        this.setFirmGroupState = this.setFirmGroupState.bind(this);
        this.updateProducts = this.updateProducts.bind(this);

        this.formInputChangeHandler = new FormInputChangeHandler(
            this.state.firmGroup,
            this.setFirmGroupState
        );
    }

    setFirmGroupState(newFirmGroup: any) {
        this.setState({ firmGroup: newFirmGroup });
    }

    componentDidMount() {
        if (this.props.item.id > 0) {
            this.props.actions
                .editBrokerFirmGroup(this.props.item.id)
                .then(() => {
                    let firmGroup = assign({}, this.props.firmGroup);
                    this.formInputChangeHandler.updateObj(firmGroup);
                    this.setFirmGroupState(firmGroup);
                })
                .catch((error: any) => {
                    notify.failure(error);
                });
        } else {
            this.props.actions
                .newBrokerFirmGroup(this.props.item.parentId)
                .then(() => {
                    let newFirmGroup = assign({}, this.state.firmGroup);
                    newFirmGroup.products = this.props.products;
                    this.formInputChangeHandler.updateObj(newFirmGroup);
                    this.setFirmGroupState(newFirmGroup);
                })
                .catch((error: any) => {
                    notify.failure(error);
                });
        }
    }

    render() {
        return (
            <Modal show={true} onHide={this.closeModal} bsSize="large">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {FormValidationService.isEdit(this.props.item.id)
                            ? Translate('broker-firm-group/form/edit/title') +
                              (this.state.firmGroup.name ? ': ' : '') +
                              this.state.firmGroup.name
                            : Translate('broker-firm-group/form/create/title')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tab.Container defaultActiveKey={1} id="uncontrolled-tab">
                        <div>
                            <Nav>
                                <NavItem eventKey={1}>
                                    {Translate('broker-firm-group/form/tab/information')}
                                    {FormValidationService.setTabValidationError(
                                        this.state.errors,
                                        'name'
                                    )}
                                </NavItem>
                                <NavItem eventKey={2}>
                                    {Translate('broker-firm-group/form/tab/product-allocations')}
                                </NavItem>
                            </Nav>
                            <Tab.Content>
                                <Tab.Pane eventKey={1}>
                                    <fieldset>
                                        <legend>
                                            {Translate('broker-firm-group/form/tab/information')}
                                        </legend>
                                        <FormGroup
                                            className="required"
                                            validationState={FormValidationService.getValidationState(
                                                this.state.errors.name
                                            )}
                                        >
                                            <ControlLabel>
                                                {Translate('broker-firm-group/form/name')}
                                            </ControlLabel>
                                            <FormControl
                                                autoFocus
                                                type="text"
                                                name="name"
                                                value={
                                                    this.state.firmGroup.name
                                                        ? this.state.firmGroup.name
                                                        : ''
                                                }
                                                error={this.state.errors.name}
                                                onChange={this.formInputChangeHandler.onChange}
                                            />
                                            <HelpBlock>{this.state.errors.name}</HelpBlock>
                                        </FormGroup>

                                        <SelectPlanTemplate
                                            onChange={this.formInputChangeHandler.onChange}
                                            planTemplates={this.props.availablePlanTemplates}
                                            planTemplateId={this.state.firmGroup.planTemplateId}
                                            defaultPlanTemplateId={this.props.defaultPlanTemplateId}
                                            errors={this.state.errors}
                                            overrideDefaultPlanTemplate={
                                                this.state.firmGroup.overrideDefaultPlanTemplate
                                            }
                                        />
                                    </fieldset>
                                </Tab.Pane>
                                <Tab.Pane eventKey={2}>
                                    <ProductList
                                        availableProductTypes={this.props.availableProductTypes}
                                        availableProducts={this.props.availableProducts}
                                        brokerTreeProductAllocationTypes={
                                            this.props.brokerTreeProductAllocationTypes
                                        }
                                        brokerTreeProductAllocationRequiredOptions={
                                            this.props.brokerTreeProductAllocationRequiredOptions
                                        }
                                        products={this.state.firmGroup.products}
                                        update={this.updateProducts}
                                    />
                                </Tab.Pane>
                            </Tab.Content>
                        </div>
                    </Tab.Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={this.closeModal}>
                        {Translate('broker-firm-group/modal/footer/button-label/close')}
                    </Button>
                    <Button disabled={this.state.creating} bsStyle="primary" onClick={this.save}>
                        {Translate('broker-firm-group/modal/footer/button-label/save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    updateProducts(products: any[]) {
        let firmGroup = assign({}, this.state.firmGroup);
        firmGroup.products = products;
        this.formInputChangeHandler.updateObj(firmGroup);
        this.setFirmGroupState(firmGroup);
    }

    save() {
        this.setState({ creating: true });
        let firmGroup = assign({}, this.state.firmGroup);

        if (firmGroup.overrideDefaultPlanTemplate === false) {
            firmGroup.planTemplateId = null;
        }

        if (firmGroup.id > 0) {
            this.update(firmGroup);
        } else {
            this.create(firmGroup);
        }
    }

    create(firmGroup) {
        this.props.actions
            .createBrokerFirmGroup(firmGroup)
            .then(() => {
                this.setState({ creating: false });
                notify.success(Translate('brokers/firmgroup/create-message'));
                this.closeModal();
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    this.setState({ errors: errors, creating: false });
                })
            );
    }

    update(firmGroup) {
        this.props.actions
            .updateBrokerFirmGroup(firmGroup.id, firmGroup)
            .then(() => {
                this.setState({ creating: false });
                notify.success(Translate('brokers/firmgroup/edit-message'));
            })
            .catch((error: any) =>
                handleError(error, (errors: any): void => {
                    this.setState({ errors: errors, creating: false });
                })
            );
    }

    closeModal() {
        this.props.onClose();
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        firmGroup: state.brokers.edit.firmGroup,
        products: state.brokers.new.products,
        availableProductTypes: Array.isArray(state.brokers.new.availableProductTypes)
            ? state.brokers.new.availableProductTypes
            : Array.isArray(state.brokers.edit.availableProductTypes)
            ? state.brokers.edit.availableProductTypes
            : [],
        availableProducts: Array.isArray(state.brokers.new.availableProducts)
            ? state.brokers.new.availableProducts
            : Array.isArray(state.brokers.edit.availableProducts)
            ? state.brokers.edit.availableProducts
            : [],
        brokerTreeProductAllocationTypes: Array.isArray(
            state.brokers.new.brokerTreeProductAllocationTypes
        )
            ? state.brokers.new.brokerTreeProductAllocationTypes
            : Array.isArray(state.brokers.edit.brokerTreeProductAllocationTypes)
            ? state.brokers.edit.brokerTreeProductAllocationTypes
            : [],
        brokerTreeProductAllocationRequiredOptions: Array.isArray(
            state.brokers.new.brokerTreeProductAllocationRequiredOptions
        )
            ? state.brokers.new.brokerTreeProductAllocationRequiredOptions
            : Array.isArray(state.brokers.edit.brokerTreeProductAllocationRequiredOptions)
            ? state.brokers.edit.brokerTreeProductAllocationRequiredOptions
            : [],
        availablePlanTemplates: Array.isArray(state.brokers.new.availablePlanTemplates)
            ? state.brokers.new.availablePlanTemplates
            : Array.isArray(state.brokers.edit.availablePlanTemplates)
            ? state.brokers.edit.availablePlanTemplates
            : [],
        defaultPlanTemplateId:
            state.brokers.edit.defaultPlanTemplateId !== undefined
                ? state.brokers.edit.defaultPlanTemplateId
                : state.brokers.new.defaultPlanTemplateId
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            newBrokerFirmGroup: (parentId: number) =>
                dispatch(BrokerFirmGroupActions.newBrokerFirmGroup(parentId)),
            createBrokerFirmGroup: (data: any) =>
                dispatch(BrokerFirmGroupActions.createBrokerFirmGroup(data)),
            editBrokerFirmGroup: (id: number) =>
                dispatch(BrokerFirmGroupActions.editBrokerFirmGroup(id)),
            updateBrokerFirmGroup: (id: number, firmGroup: any) =>
                dispatch(BrokerFirmGroupActions.updateBrokerFirmGroup(id, firmGroup))
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FirmGroupModal);
